import React from "react";
import { Modal, InputNumber, Input, Checkbox } from "antd";
import styled from "styled-components";
import { Question } from "../types";
import QuestionPreviewComponent from "./QuestionPreviewComponent";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-body {
    padding: 24px;
  }
  .ant-modal-close {
    top: 16px;
    right: 16px;
  }
`;

const ContentWrapper = styled.div`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
`;

const HeaderSection = styled.div`
  margin-bottom: 20px;
  .subject {
    font-size: 14px;
    font-weight: 800;
    color: #434343;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .section-info {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 4px;
    font-weight: 500;
  }
`;

const QuestionBox = styled.div`
  background: #fdfdfd;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 15px;
  line-height: 1.6;
  color: #262626;
`;

const MarksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  .mark-item {
    .label {
      display: block;
      font-size: 12px;
      font-weight: 700;
      color: #595959;
      margin-bottom: 8px;
    }
    .input-row {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #fff;
      border: 1px solid #d9d9d9;
      padding: 4px 12px;
      border-radius: 8px;
      transition: all 0.3s;
      &:focus-within {
        border-color: #1890ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      }
    }
  }
`;

const CommentSection = styled.div`
  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #595959;
    margin-bottom: 8px;
    svg {
      color: #8c8c8c;
    }
  }
  textarea {
    border-radius: 8px;
    padding: 12px;
    background: #f5f5f5;
    border: none;
    font-size: 14px;
    &:focus {
      background: #fff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }
`;

interface QuestionEvaluationModalProps {
  visible: boolean;
  onClose: () => void;
  question: Question | null;
  subjectName: string;
  sectionName: string;
  onMarksChange: (qId: number, pId: number | null, val: number | null) => void;
  onCommentChange: (qId: number, pId: number | null, val: string) => void;
  onAttemptedChange: (qId: number, val: boolean) => void;
  onAIEvaluatedMarksChange: (
    qId: number,
    pId: number | null,
    val: number | null
  ) => void;
  isParent?: boolean;
  alternates?: Question[];
}

const QuestionEvaluationModal: React.FC<QuestionEvaluationModalProps> = ({
  visible,
  onClose,
  question,
  subjectName,
  sectionName,
  onMarksChange,
  onCommentChange,
  onAttemptedChange,
  onAIEvaluatedMarksChange,
  isParent,
}) => {
  if (!question) return null;

  let questionData = {};
  if (question.questionContent) {
    try {
      questionData = JSON.parse(question.questionContent);
    } catch (e) {
      questionData = { text: question.questionContent };
    }
  }

  return (
    <StyledModal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
      maskStyle={{ backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.4)" }}
    >
      <ContentWrapper>
        <HeaderSection>
          <div className="subject">
            {subjectName} • Q {question.questionNumber}
          </div>
          <div className="section-info">
            {sectionName} | {question.obtainedMarks || 0}/
            {question.totalMarks || 0} marks
          </div>
        </HeaderSection>

        <QuestionBox>
          <QuestionPreviewComponent questionData={questionData} />
        </QuestionBox>

        {!isParent && (
          <>
            {question.parts && question.parts.length > 0 ? (
              <div style={{ marginBottom: "20px" }}>
                {question.parts.map((part) => (
                  <div
                    key={part.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                      background: "#fff",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d9d9d9",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#595959",
                      }}
                    >
                      {part.label}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <InputNumber
                        value={part.marks}
                        min={0}
                        max={part.total}
                        bordered={false}
                        size="small"
                        style={{ width: "60px", textAlign: "right" }}
                        onChange={(val) =>
                          onMarksChange(question.id, part.id, val)
                        }
                      />
                      <span style={{ color: "#bfbfbf", fontSize: "13px" }}>
                        /{part.total}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <MarksGrid>
                <div className="mark-item">
                  <span className="label">Faculty Mark</span>
                  <div className="input-row">
                    <InputNumber
                      value={question.obtainedMarks}
                      min={0}
                      max={question.totalMarks}
                      bordered={false}
                      style={{ width: "100%" }}
                      onChange={(val) => onMarksChange(question.id, null, val)}
                    />
                    <span style={{ color: "#bfbfbf", fontSize: "14px" }}>
                      /{question.totalMarks}
                    </span>
                  </div>
                </div>

                <div className="mark-item">
                  <span className="label">AI Mark</span>
                  <div className="input-row" style={{ background: "#f5f5f5" }}>
                    <InputNumber
                      value={question.aiEvaluatedMarks}
                      min={0}
                      max={question.totalMarks}
                      disabled
                      bordered={false}
                      style={{ width: "100%" }}
                      onChange={(val) =>
                        onAIEvaluatedMarksChange(question.id, null, val)
                      }
                    />
                    <span style={{ color: "#bfbfbf", fontSize: "14px" }}>
                      /{question.totalMarks}
                    </span>
                  </div>
                </div>
              </MarksGrid>
            )}

            <div style={{ marginBottom: "20px" }}>
              <Checkbox
                checked={question.isAttempted === false}
                onChange={(e) =>
                  onAttemptedChange(question.id, !e.target.checked)
                }
              >
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#595959",
                  }}
                >
                  Not Attempted
                </span>
              </Checkbox>
            </div>

            <CommentSection>
              <div className="label">
                <span>💬 Add Q. Comment</span>
              </div>
              <Input.TextArea
                value={question.evaluatedComment}
                placeholder="..."
                autoSize={{ minRows: 3, maxRows: 5 }}
                onChange={(e) =>
                  onCommentChange(question.id, null, e.target.value)
                }
              />
            </CommentSection>
          </>
        )}
      </ContentWrapper>
    </StyledModal>
  );
};

export default QuestionEvaluationModal;
